import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { isFeatureEnabled, getFeatureMode } from "@/pages/dev-panel/page";

// ── Save-based Undo System ────────────────────────────────────────────────────
// Sadece "Kaydet" tuşuna basıldığında snapshot alınır.
// Her keystroke'ta değil — sadece kaydetme anları geri alınabilir.

interface SaveSnapshot {
  fieldValues: Record<string, string>;
  savedAt: number;
}

const saveSnapshots: Record<string, SaveSnapshot[]> = {};

function pushSnapshot(key: string, values: Record<string, string>) {
  if (!saveSnapshots[key]) saveSnapshots[key] = [];
  saveSnapshots[key].push({ fieldValues: { ...values }, savedAt: Date.now() });
  if (saveSnapshots[key].length > 20) saveSnapshots[key].shift();
}

function popSnapshot(key: string): Record<string, string> | null {
  const snaps = saveSnapshots[key];
  if (!snaps || snaps.length === 0) return null;
  return { ...snaps.pop()!.fieldValues };
}

function hasSnapshot(key: string): boolean {
  return (saveSnapshots[key]?.length ?? 0) > 0;
}

// ── Undo availability (global toggle from dev panel) ─────────────────────────

function getUndoEnabled(): boolean {
  try {
    const raw = localStorage.getItem("yg_feature_toggles");
    if (raw) {
      const states = JSON.parse(raw) as Record<string, boolean>;
      if ("undo_enabled" in states) return states["undo_enabled"];
    }
  } catch { /**/ }
  // Default: enabled (true)
  const raw2 = localStorage.getItem("yg_feature_toggles");
  return raw2 ? true : true;
}

// ── Locked undo usage (3 per 24h when undo disabled) ────────────────────────

const UNDO_LOCKED_KEY = "yg_undo_locked_usage";
const MAX_LOCKED_UNDOS = 3;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface LockedUndoUsage {
  count: number;
  resetAt: number;
}

function getLockedUndoUsage(): LockedUndoUsage {
  try {
    const raw = localStorage.getItem(UNDO_LOCKED_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as LockedUndoUsage;
      if (Date.now() > parsed.resetAt) {
        // Süre dolmuş, sıfırla
        return { count: 0, resetAt: Date.now() + ONE_DAY_MS };
      }
      return parsed;
    }
  } catch { /**/ }
  return { count: 0, resetAt: Date.now() + ONE_DAY_MS };
}

function getRemainingLockedUndos(): number {
  const usage = getLockedUndoUsage();
  if (Date.now() > usage.resetAt) return MAX_LOCKED_UNDOS;
  return Math.max(0, MAX_LOCKED_UNDOS - usage.count);
}

function consumeLockedUndo(): void {
  const usage = getLockedUndoUsage();
  const updated: LockedUndoUsage = {
    count: usage.count + 1,
    resetAt: Date.now() > usage.resetAt ? Date.now() + ONE_DAY_MS : usage.resetAt,
  };
  localStorage.setItem(UNDO_LOCKED_KEY, JSON.stringify(updated));
}

// ── Inline Field type ─────────────────────────────────────────────────────────

export interface InlineField {
  key: string;
  label: string;
  value: string;
  multiline?: boolean;
  hint?: string;
}

// ── Popup Edit Modal ──────────────────────────────────────────────────────────

interface PopupEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  label: string;
  icon: string;
  borderColor: string;
  fields: InlineField[];
  historyKey: string;
  onSave: (fields: Record<string, string>) => Promise<void> | void;
  onGoAdmin: () => void;
  /** Deprecated — still accepted for backwards compat but undo is now global */
  lockedUndoCount?: number;
  isLocked?: boolean;
}

function PopupEditModal({
  isOpen, onClose, label, icon, borderColor, fields, historyKey,
  onSave, onGoAdmin, isLocked = false,
}: PopupEditModalProps) {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeField, setActiveField] = useState<string | null>(null);

  // Undo availability
  const undoEnabled = getUndoEnabled();
  const remainingLocked = !undoEnabled ? getRemainingLockedUndos() : MAX_LOCKED_UNDOS;
  const canUndoNow = hasSnapshot(historyKey) && (undoEnabled || remainingLocked > 0);

  // Fields yükle
  useEffect(() => {
    if (!isOpen) return;
    const initial: Record<string, string> = {};
    fields.forEach(f => { initial[f.key] = f.value; });
    setFieldValues(initial);
    setSaved(false);
    setActiveField(null);
  }, [isOpen, fields]);

  const handleFieldChange = (key: string, val: string) => {
    // Sadece görsel güncelleme — undo snapshot ALMIYORUZ
    setFieldValues(prev => ({ ...prev, [key]: val }));
  };

  const handleSave = useCallback(async () => {
    // ✅ Kaydet anında snapshot al (bir önceki kaydedilmiş hali sakla)
    pushSnapshot(historyKey, fieldValues);
    setSaving(true);
    try {
      await onSave(fieldValues);
      setSaved(true);
      setTimeout(() => { setSaved(false); onClose(); }, 1200);
    } catch { /**/ }
    finally { setSaving(false); }
  }, [historyKey, fieldValues, onSave, onClose]);

  const handleUndo = useCallback(async () => {
    if (!hasSnapshot(historyKey)) return;

    if (!undoEnabled) {
      const remaining = getRemainingLockedUndos();
      if (remaining <= 0) return;
      consumeLockedUndo();
    }

    const previousValues = popSnapshot(historyKey);
    if (!previousValues) return;

    // Form alanlarını güncelle
    setFieldValues(previousValues);

    // Geri alınan değeri hemen kaydet (site'ye yansısın)
    try {
      await onSave(previousValues);
    } catch { /**/ }
  }, [historyKey, undoEnabled, onSave]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === "z") { e.preventDefault(); handleUndo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); handleSave(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, handleUndo, handleSave, onClose]);

  if (!isOpen) return null;

  const visibleFields = activeField ? fields.filter(f => f.key === activeField) : fields;
  const snapshotCount = saveSnapshots[historyKey]?.length ?? 0;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ background: "rgba(5,10,20,0.85)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="w-full max-w-lg bg-[#0F1729] rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        style={{ border: `2px solid ${borderColor}`, maxHeight: "min(85vh, 700px)" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E2B4A] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: borderColor + "25" }}>
              <i className={`${icon} text-base`} style={{ color: borderColor }}></i>
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{label}</p>
              <p className="text-[#5E7398] text-xs">{fields.length} düzenlenebilir alan</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Geri al butonu */}
            <button
              onClick={handleUndo}
              disabled={!canUndoNow}
              title={
                !undoEnabled
                  ? `Geri Al (Ctrl+Z) — ${remainingLocked} hak kaldı / 24 saat`
                  : `Geri Al (Ctrl+Z) — ${snapshotCount} kaydetme noktası`
              }
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap border ${
                canUndoNow
                  ? "bg-amber-500/15 text-amber-400 border-amber-500/30 hover:bg-amber-500/25 cursor-pointer"
                  : "bg-white/5 text-[#465886] border-[#2B3654] opacity-50 cursor-not-allowed"
              }`}
            >
              <i className="ri-arrow-go-back-line text-xs"></i>
              <span>Geri Al</span>
              {canUndoNow && (
                <span className="bg-amber-500/30 text-amber-300 px-1 rounded-sm text-xs">
                  {undoEnabled ? snapshotCount : remainingLocked}
                </span>
              )}
              {!undoEnabled && (
                <i className="ri-lock-fill text-amber-500/60 text-xs ml-0.5" title="Kilitli mod — 3/gün"></i>
              )}
            </button>

            <button
              onClick={onGoAdmin}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-[#2B3654] text-[#7B8FAF] rounded-lg hover:text-white cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-external-link-line text-xs"></i>
              Tam Editör
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center text-[#7B8FAF] hover:text-white rounded-lg hover:bg-white/5 cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>

        {/* Kilitli uyarı */}
        {isLocked && (
          <div className="mx-5 mt-3 flex items-start gap-2 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2.5 flex-shrink-0">
            <i className="ri-lock-fill text-amber-400 text-sm flex-shrink-0 mt-0.5"></i>
            <p className="text-amber-400/90 text-xs leading-relaxed">
              Bu alan kilitli. Geliştirici panelinden açılabilir.
            </p>
          </div>
        )}

        {/* Geri al kapalıyken bilgi */}
        {!undoEnabled && (
          <div className="mx-5 mt-3 flex items-center gap-2 bg-[#141E35] border border-[#2B3654] rounded-xl px-3 py-2 flex-shrink-0">
            <i className="ri-information-line text-[#7B8FAF] text-xs flex-shrink-0"></i>
            <p className="text-[#7B8FAF] text-xs">
              Geri Al: <span className="text-amber-400 font-medium">{remainingLocked}/{MAX_LOCKED_UNDOS} hak</span> (24 saatte bir yenilenir)
            </p>
          </div>
        )}

        {/* Field Filter Tabs */}
        {fields.length > 4 && (
          <div className="flex gap-1.5 px-5 pt-3 pb-1 overflow-x-auto flex-shrink-0">
            <button
              onClick={() => setActiveField(null)}
              className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap cursor-pointer flex-shrink-0 border transition-colors ${
                activeField === null
                  ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                  : "text-[#7B8FAF] hover:text-white border-[#2B3654]"
              }`}
            >
              Tümü ({fields.length})
            </button>
            {fields.map(f => (
              <button
                key={f.key}
                onClick={() => setActiveField(f.key)}
                className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap cursor-pointer flex-shrink-0 border transition-colors ${
                  activeField === f.key
                    ? "bg-[#3B82F6]/30 text-white border-[#3B82F6]/50"
                    : "text-[#7B8FAF] hover:text-white border-[#2B3654]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Fields */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3.5 min-h-0">
          {visibleFields.map(field => (
            <div key={field.key}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[#7B8FAF] text-xs uppercase tracking-widest">{field.label}</label>
                <span className="text-[#465886] text-xs">
                  {(fieldValues[field.key] ?? field.value ?? "").length} karakter
                </span>
              </div>
              {field.multiline ? (
                <textarea
                  value={fieldValues[field.key] ?? field.value}
                  onChange={e => handleFieldChange(field.key, e.target.value)}
                  rows={3}
                  className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-xl px-3 py-2.5 outline-none focus:border-emerald-500 resize-none transition-colors"
                />
              ) : (
                <input
                  type="text"
                  value={fieldValues[field.key] ?? field.value}
                  onChange={e => handleFieldChange(field.key, e.target.value)}
                  className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-xl px-3 py-2.5 outline-none focus:border-emerald-500 transition-colors"
                />
              )}
              {field.hint && <p className="text-[#465886] text-xs mt-1">{field.hint}</p>}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex gap-2.5 px-5 py-4 border-t border-[#1E2B4A] flex-shrink-0">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 ${
              saved ? "bg-emerald-500 text-white" : "text-white disabled:opacity-60"
            }`}
            style={!saved ? { background: borderColor } : {}}
          >
            {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
            {saved
              ? <><i className="ri-check-double-line"></i> Kaydedildi!</>
              : saving
                ? "Kaydediliyor..."
                : <><i className="ri-save-line"></i> Kaydet</>
            }
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm border border-[#2B3654] text-[#7B8FAF] rounded-xl hover:text-white cursor-pointer whitespace-nowrap transition-colors"
          >
            İptal
          </button>
        </div>

        {/* Keyboard shortcuts */}
        <div className="flex items-center justify-center gap-4 px-5 pb-3 flex-shrink-0">
          {[
            { key: "Ctrl+S", label: "Kaydet" },
            { key: "Ctrl+Z", label: "Geri Al" },
            { key: "Esc",    label: "Kapat" },
          ].map(s => (
            <span key={s.key} className="text-[#465886] text-xs flex items-center gap-1">
              <kbd className="bg-[#1E2B4A] px-1.5 py-0.5 rounded text-[10px] text-[#7B8FAF]">{s.key}</kbd>
              {s.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Section Overlay ───────────────────────────────────────────────────────────

interface SectionOverlayProps {
  children: React.ReactNode;
  label: string;
  description: string;
  tab: string;
  borderColor: string;
  bgColor: string;
  badgeBg: string;
  badgeText: string;
  icon: string;
  itemId?: number | string;
  fieldCount?: number;
  lastChanged?: string;
  sizeHint?: string;
}

export default function SectionOverlay({
  children, label, description, tab, borderColor, bgColor, badgeBg, badgeText, icon, itemId,
  lastChanged, sizeHint,
}: SectionOverlayProps) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const handleClick = () => {
    const url = itemId !== undefined
      ? `/kontrol-paneli?tab=${tab}&id=${itemId}`
      : `/kontrol-paneli?tab=${tab}`;
    navigate(url);
  };

  return (
    <div
      className="relative transition-all duration-200"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ pointerEvents: "none" }}>{children}</div>

      <div
        className={`absolute inset-0 z-20 cursor-pointer transition-all duration-200 ${hovered ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{ border: `3px solid ${borderColor}`, background: bgColor }}
        onClick={handleClick}
      >
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full shadow-lg" style={{ background: badgeBg }}>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className={`${icon} text-xs`} style={{ color: badgeText }}></i>
            </div>
            <span className="text-xs font-semibold whitespace-nowrap" style={{ color: badgeText }}>{label}</span>
          </div>
          {sizeHint && (
            <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-black/40">
              <span className="text-white text-xs">{sizeHint}</span>
            </div>
          )}
        </div>

        {lastChanged && (
          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/40">
            <i className="ri-time-line text-white/70 text-xs"></i>
            <span className="text-white/70 text-xs whitespace-nowrap">{lastChanged}</span>
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={handleClick}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm shadow-xl cursor-pointer hover:opacity-90 active:scale-95 transition-all"
            style={{ background: borderColor, color: "#fff" }}
          >
            <i className="ri-pencil-line text-base"></i>
            <span className="whitespace-nowrap">Düzenle</span>
            <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>

        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
          <span className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(0,0,0,0.5)", color: "#e2e8f0" }}>
            {description}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Inspector Card (with PopupEditModal) ─────────────────────────────────────

interface InspectorCardProps {
  children: React.ReactNode;
  label: string;
  description: string;
  tab: string;
  borderColor: string;
  bgColor: string;
  badgeBg: string;
  badgeText: string;
  icon: string;
  itemId?: number | string;
  inlineEditFields?: InlineField[];
  onInlineSave?: (fields: Record<string, string>) => void;
  meta?: Array<{ icon: string; value: string; color?: string }>;
  completeness?: number;
  featureId?: string;
}

export function InspectorCard({
  children, label, description, tab, borderColor, bgColor, badgeBg, badgeText, icon, itemId,
  inlineEditFields, onInlineSave, meta, completeness, featureId,
}: InspectorCardProps) {
  const [hovered, setHovered] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const navigate = useNavigate();

  const featureEnabled = featureId ? isFeatureEnabled(featureId) : true;
  const featureMode = featureId ? getFeatureMode(featureId) : "hide";
  const isLocked = !featureEnabled && featureMode === "lock";

  const histKey = `insp-card-${tab}-${itemId ?? label}`;

  const handleGoAdmin = () => {
    const url = itemId !== undefined
      ? `/kontrol-paneli?tab=${tab}&id=${itemId}`
      : `/kontrol-paneli?tab=${tab}`;
    navigate(url);
  };

  const handleSave = (fields: Record<string, string>) => {
    onInlineSave?.(fields);
  };

  const filledFields = inlineEditFields
    ? inlineEditFields.filter(f => f.value?.trim().length > 0).length : 0;
  const totalFields = inlineEditFields?.length || 0;
  const calcCompleteness = completeness !== undefined
    ? completeness
    : (totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 100);

  return (
    <>
      <div
        className="relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => !popupOpen && setHovered(false)}
      >
        <div style={{ pointerEvents: "none" }}>{children}</div>

        {calcCompleteness < 100 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200/10 z-10">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${calcCompleteness}%`,
                background: calcCompleteness < 50 ? "#F43F5E" : calcCompleteness < 80 ? "#F59E0B" : "#10B981",
              }}
            />
          </div>
        )}

        <div
          className={`absolute inset-0 z-20 transition-all duration-200 ${hovered && !popupOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          style={{ border: `2px solid ${borderColor}`, background: bgColor }}
        >
          <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-full" style={{ background: badgeBg }}>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`${icon} text-xs`} style={{ color: badgeText }}></i>
              </div>
              <span className="text-xs font-semibold whitespace-nowrap" style={{ color: badgeText }}>{label}</span>
              {isLocked && <i className="ri-lock-fill text-amber-400 text-xs ml-1"></i>}
            </div>
            {calcCompleteness < 100 && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40">
                <span className="text-xs font-medium" style={{ color: calcCompleteness < 50 ? "#F87171" : "#FBBF24" }}>
                  %{calcCompleteness}
                </span>
              </div>
            )}
          </div>

          {meta && meta.length > 0 && (
            <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
              {meta.map((m, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/40">
                  <i className={`${m.icon} text-xs`} style={{ color: m.color || "#94A3B8" }}></i>
                  <span className="text-xs whitespace-nowrap" style={{ color: m.color || "#94A3B8" }}>{m.value}</span>
                </div>
              ))}
            </div>
          )}

          <div className="absolute inset-0 flex items-center justify-center gap-3">
            {/* Kilitli bölüm bilgi kutusu */}
            {isLocked ? (
              <div className="bg-[#070D1F]/95 border border-amber-500/50 rounded-2xl p-5 max-w-[280px] text-center shadow-2xl backdrop-blur-sm">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/30">
                  <i className="ri-lock-fill text-amber-400 text-2xl"></i>
                </div>
                <p className="text-white font-bold text-sm mb-1.5">{label}</p>
                <p className="text-[#A5B5CC] text-xs leading-relaxed mb-3">{description}</p>
                <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2.5">
                  <p className="text-amber-300 text-xs font-semibold leading-relaxed">
                    <i className="ri-information-line mr-1"></i>
                    Aktifleştirmek için lütfen geliştirici ile iletişime geçin.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {inlineEditFields && inlineEditFields.length > 0 && (
                  <button
                    onClick={() => setPopupOpen(true)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm shadow-xl cursor-pointer whitespace-nowrap transition-all hover:opacity-90 active:scale-95"
                    style={{ background: "#10B981", color: "#fff" }}
                  >
                    <i className="ri-pencil-fill text-sm"></i>
                    Düzenle
                  </button>
                )}
                <button
                  onClick={handleGoAdmin}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm shadow-xl cursor-pointer whitespace-nowrap transition-all hover:opacity-90 active:scale-95"
                  style={{ background: borderColor, color: "#fff" }}
                >
                  <i className="ri-settings-3-line text-sm"></i>
                  Tam Editör
                </button>
              </>
            )}
          </div>

          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
            <span className="text-xs px-2 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.6)", color: "#e2e8f0" }}>
              {description}
            </span>
          </div>
        </div>
      </div>

      <PopupEditModal
        isOpen={popupOpen}
        onClose={() => { setPopupOpen(false); setHovered(false); }}
        label={label}
        icon={icon}
        borderColor={borderColor}
        fields={inlineEditFields || []}
        historyKey={histKey}
        onSave={handleSave}
        onGoAdmin={handleGoAdmin}
        isLocked={isLocked}
      />
    </>
  );
}

export { PopupEditModal };
export type { PopupEditModalProps };
